import { useEffect, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Eye, EyeOff, Pin, PinOff, Plus, Settings2, Star, Trash2, X } from 'lucide-react';
import {
  editableNumber,
  parseDecimal,
  FIELD_TYPE_LABELS,
  FIELD_TYPES,
  LABEL_COLORS,
  MAX_PINNED_FIELDS,
  DEFAULT_APPROVAL,
  AGGREGATE_LABEL,
  aggregateOptions,
  CURRENCIES,
  formatFieldFormula,
  formatNumberOf,
  formulaGap,
  formulaGapText,
  isNumeric,
  parseFieldFormula,
  type AggregateKind,
  formatSequence,
  type SequenceConfig,
  type ApprovalConfig,
  type FieldConfig,
  type BoardSnapshot,
  type CardDetail,
  type CustomField,
  type FieldOption,
  type FieldType,
  type LabelColor,
} from '@kaba/shared';
import { withFormConfirm } from '../../lib/formDeps';
import { saveCardField } from '../../lib/cardFields';
import { api, newOpId } from '../../lib/api';
import { boardKey, useBoardAction } from '../../lib/board';
import { toast } from '../../lib/toast';
import { Button, cx, LABEL_BG, Sheet } from '../ui/ui';
import { Section, SectionAction } from './Section';
import { ApprovalBox, ApprovalSettings } from './Approval';

const inputCls =
  'min-h-11 w-full rounded-lg border border-[var(--line)] bg-[var(--panel)] px-2.5 text-base outline-none focus:border-accent sm:min-h-10 sm:text-sm';

/** Eigene Felder in der Kartenansicht. Jeder Wert wird einzeln gespeichert. */
export function CustomFields({
  card,
  snapshot,
  canEdit,
  pinnedOnly,
  id,
  onShowAll,
}: {
  card: CardDetail;
  snapshot: BoardSnapshot;
  canEdit: boolean;
  /** Nur die angehefteten Felder (Abkürzung in der Übersicht, dieselben Werte) */
  pinnedOnly?: boolean;
  id?: string;
  /** Übersicht: zu allen Feldern unter „Details“ */
  onShowAll?: () => void;
}) {
  const qc = useQueryClient();
  const [manage, setManage] = useState(false);
  const fields = pinnedOnly ? snapshot.fields.filter((f) => f.pinned) : snapshot.fields;
  if (!fields.length && (!canEdit || pinnedOnly)) return null;

  const setValue = (field: CustomField, value: unknown) => saveCardField(qc, snapshot.board.id, card, field, value);

  if (pinnedOnly)
    return (
      <Section
        title="Eigene Felder"
        aside={onShowAll ? <SectionAction onClick={onShowAll}>Alle Felder →</SectionAction> : undefined}
        bodyClass="grid grid-cols-1 gap-3 sm:grid-cols-2"
      >
        {fields.map((f) =>
          f.type === 'approval' ? (
            <div key={f.id} className="sm:col-span-2">
              <ApprovalBox field={f} card={card} snapshot={snapshot} canEdit={canEdit} />
            </div>
          ) : (
            <label key={f.id} className={cx('flex flex-col gap-1', f.type === 'longtext' && 'sm:col-span-2')}>
              <span className="text-sm font-medium">{f.name}</span>
              <FieldInput field={f} value={card.custom[f.id] ?? null} canEdit={canEdit} members={snapshot.members} onChange={(v) => void setValue(f, v)} gap={f.type === 'formula' ? formulaGapText(formulaGap(snapshot.fields, card.custom, f.id)) : null} />
            </label>
          ),
        )}
      </Section>
    );

  return (
    <Section
      id={id}
      title="Eigene Felder"
      aside={
        canEdit ? (
          <SectionAction onClick={() => setManage(true)}>
            <Settings2 size={15} aria-hidden /> {fields.length ? 'Felder verwalten' : 'Feld anlegen'}
          </SectionAction>
        ) : undefined
      }
    >
      {fields.length === 0 && (
        <p className="text-sm text-[var(--ink-soft)]">Lege Felder wie Budget, Kunde, Ort oder Status für alle Karten dieses Boards an.</p>
      )}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        {/* 0.89 (K4): jedes Feld ist ein Sprungziel für Handlungshinweise (`kd-feld-<id>`) */}
        {fields.map((f) =>
          f.type === 'approval' ? (
            <div key={f.id} id={id ? `kd-feld-${f.id}` : undefined} tabIndex={-1} className="rounded-lg outline-none focus-visible:ring-2 focus-visible:ring-accent/40 sm:col-span-2">
              <ApprovalBox field={f} card={card} snapshot={snapshot} canEdit={canEdit} />
            </div>
          ) : (
            <label key={f.id} id={id ? `kd-feld-${f.id}` : undefined} tabIndex={-1} className={cx('flex flex-col gap-1 rounded-lg outline-none focus-visible:ring-2 focus-visible:ring-accent/40', f.type === 'longtext' && 'sm:col-span-2')}>
              <span className="text-sm font-medium">{f.name}</span>
              <FieldInput field={f} value={card.custom[f.id] ?? null} canEdit={canEdit} members={snapshot.members} onChange={(v) => void setValue(f, v)} gap={f.type === 'formula' ? formulaGapText(formulaGap(snapshot.fields, card.custom, f.id)) : null} />
            </label>
          ),
        )}
      </div>
      <FieldManager snapshot={snapshot} open={manage} onClose={() => setManage(false)} />
    </Section>
  );
}

export function FieldInput({
  field,
  value,
  canEdit,
  members,
  onChange,
  gap,
}: {
  field: CustomField;
  value: unknown;
  canEdit: boolean;
  members: BoardSnapshot['members'];
  onChange: (v: unknown) => void;
  /** 0.86: berechnetes Feld leer – warum (fehlender Wert oder Rechenfehler) */
  gap?: string | null;
}) {
  // Zahlen in deutscher Schreibweise bearbeiten („12,5“); parseDecimal liest das verlustfrei zurück
  const shown =
    value == null
      ? ''
      : (field.type === 'number' || field.type === 'currency') && typeof value === 'number'
        ? editableNumber(value, field.type === 'currency')
        : String(value);
  const [draft, setDraft] = useState(shown);
  useEffect(() => setDraft(shown), [shown]);
  const commit = () => {
    if (draft === shown) return;
    if ((field.type === 'number' || field.type === 'currency') && draft.trim() && parseDecimal(draft) === null) {
      toast.error(`„${draft}“ ist keine gültige Zahl.`);
      setDraft(shown);
      return;
    }
    onChange(draft);
  };
  const text = (type: string, extra?: Record<string, unknown>) => (
    <input
      className={inputCls}
      type={type}
      value={draft}
      readOnly={!canEdit}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => e.key === 'Enter' && (e.target as HTMLInputElement).blur()}
      {...extra}
    />
  );
  switch (field.type) {
    case 'text':
      return text('text');
    case 'phone':
      return text('tel', { inputMode: 'tel' });
    case 'email':
      return text('email', { inputMode: 'email' });
    case 'url':
      return (
        <div className="flex gap-1.5">
          {text('url', { inputMode: 'url', placeholder: 'https://' })}
          {typeof value === 'string' && (
            <a href={value} target="_blank" rel="noopener noreferrer nofollow" className="inline-flex min-h-10 items-center rounded-lg px-2.5 text-sm text-accent-fg">
              Öffnen
            </a>
          )}
        </div>
      );
    case 'number':
    case 'currency': {
      // 0.82: Einheit bzw. Währung als Zusatz im Feld
      const unit = field.type === 'currency' ? (field.config?.currency && field.config.currency !== 'EUR' ? field.config.currency : '€') : field.config?.unit?.trim();
      if (!unit) return text('text', { inputMode: 'decimal' });
      return (
        <div className="relative">
          {text('text', { inputMode: 'decimal', style: { paddingRight: `${Math.max(2, unit.length * 0.6 + 1.2)}rem` } })}
          <span className="pointer-events-none absolute top-1/2 right-3 -translate-y-1/2 text-sm text-[var(--ink-soft)]">{unit}</span>
        </div>
      );
    }
    case 'formula':
      return (
        <p className="min-h-10 py-2 text-sm tabular-nums" title="Berechnet – ändert sich mit den Werten der Formel">
          {typeof value === 'number' ? formatNumberOf(field, value) : <span className={cx('text-xs', gap?.startsWith('Rechenfehler') ? 'text-rose-600' : 'text-[var(--ink-soft)]')}>{gap ?? '–'}</span>}
        </p>
      );
    case 'date':
      return <input className={inputCls} type="date" value={draft} disabled={!canEdit} onChange={(e) => onChange(e.target.value)} />;
    case 'longtext':
      return (
        <textarea
          className={cx(inputCls, 'min-h-20 py-2')}
          value={draft}
          readOnly={!canEdit}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commit}
        />
      );
    case 'checkbox':
      return (
        <input
          type="checkbox"
          checked={value === true}
          disabled={!canEdit}
          onChange={(e) => onChange(e.target.checked)}
          className="size-6 accent-[var(--color-accent)]"
        />
      );
    case 'rating':
      return (
        <div className="flex gap-0.5" role="radiogroup" aria-label={field.name}>
          {[1, 2, 3, 4, 5].map((n) => (
            <button
              key={n}
              type="button"
              role="radio"
              aria-checked={value === n}
              aria-label={`${n} von 5`}
              disabled={!canEdit}
              onClick={() => onChange(value === n ? null : n)}
              className="inline-flex size-10 items-center justify-center"
            >
              <Star size={22} className={Number(value) >= n ? 'fill-amber-400 text-amber-400' : 'text-[var(--line)]'} />
            </button>
          ))}
        </div>
      );
    case 'select':
      return (
        <select className={inputCls} value={(value as string) ?? ''} disabled={!canEdit} onChange={(e) => onChange(e.target.value || null)}>
          <option value="">–</option>
          {field.options.map((o) => (
            <option key={o.id} value={o.id}>
              {o.label}
            </option>
          ))}
        </select>
      );
    case 'multiselect': {
      const selected = Array.isArray(value) ? (value as string[]) : [];
      return (
        <div className="flex flex-wrap gap-1.5">
          {field.options.map((o) => {
            const on = selected.includes(o.id);
            return (
              <button
                key={o.id}
                type="button"
                aria-pressed={on}
                disabled={!canEdit}
                onClick={() => onChange(on ? selected.filter((x) => x !== o.id) : [...selected, o.id])}
                className={cx(
                  'inline-flex min-h-9 items-center gap-1.5 rounded-lg border px-2.5 text-sm',
                  on ? cx(LABEL_BG[o.color], 'border-transparent text-white') : 'border-[var(--line)]',
                )}
              >
                {!on && <span className={cx('size-2.5 rounded-full', LABEL_BG[o.color])} />}
                {o.label}
              </button>
            );
          })}
        </div>
      );
    }
    case 'approval':
      return null;
    case 'sequence':
      return <p className="min-h-10 py-2 font-mono text-sm tabular-nums">{value ? String(value) : '–'}</p>;
    case 'person':
      return (
        <select className={inputCls} value={(value as string) ?? ''} disabled={!canEdit} onChange={(e) => onChange(e.target.value || null)}>
          <option value="">–</option>
          {members.map((m) => (
            <option key={m.id} value={m.id}>
              {m.name}
            </option>
          ))}
        </select>
      );
  }
}

/* ---------- Felder verwalten ---------- */

function FieldManager({ snapshot, open, onClose }: { snapshot: BoardSnapshot; open: boolean; onClose: () => void }) {
  const boardId = snapshot.board.id;
  const action = useBoardAction(boardId);
  const qc = useQueryClient();
  const [editing, setEditing] = useState<CustomField | 'new' | null>(null);
  const isOwner = snapshot.board.role === 'owner';
  const pinnedCount = snapshot.fields.filter((f) => f.pinned).length;

  return (
    <Sheet open={open} onOpenChange={(o) => !o && (setEditing(null), onClose())} title="Eigene Felder" side="center">
      {editing ? (
        <FieldForm
          field={editing === 'new' ? null : editing}
          snapshot={snapshot}
          isOwner={isOwner}
          canPin={pinnedCount < MAX_PINNED_FIELDS || (editing !== 'new' && editing.pinned)}
          onCancel={() => setEditing(null)}
          onSave={(data) => {
            action.mutate(async () => {
              if (editing === 'new') await api.post(`/api/boards/${boardId}/fields`, data);
              else {
                const { type: _t, ...rest } = data;
                // Freigabe-Einstellungen ändern nur Besitzer*innen (sonst gar nicht mitschicken)
                if (editing.type === 'approval' && !isOwner) delete rest.config;
                await withFormConfirm((q) => api.patch(`/api/fields/${editing.id}${q}`, rest));
              }
              await qc.invalidateQueries({ queryKey: boardKey(boardId) });
            });
            setEditing(null);
          }}
        />
      ) : (
        <div className="flex flex-col gap-3 p-4">
          <ul className="flex flex-col divide-y divide-[var(--line)]">
            {snapshot.fields.map((f) => (
              <li key={f.id} className="flex min-h-12 items-center gap-2">
                <button className="flex-1 text-left" onClick={() => setEditing(f)}>
                  <span className="block text-sm font-medium">{f.name}</span>
                  <span className="text-xs text-[var(--ink-soft)]">
                    {FIELD_TYPE_LABELS[f.type]}
                    {f.showOnCard ? ' · auf der Karte sichtbar' : ''}
                    {f.pinned ? ' · in der Übersicht angeheftet' : ''}
                  </span>
                </button>
                <button
                  aria-label={f.pinned ? `${f.name} in der Übersicht lösen` : `${f.name} in der Übersicht anheften`}
                  aria-pressed={f.pinned}
                  title={
                    f.pinned
                      ? 'In der Übersicht angeheftet'
                      : pinnedCount >= MAX_PINNED_FIELDS
                        ? `Höchstens ${MAX_PINNED_FIELDS} Felder lassen sich anheften`
                        : 'In der Übersicht der Kartendetails anheften'
                  }
                  disabled={!f.pinned && pinnedCount >= MAX_PINNED_FIELDS}
                  className="inline-flex size-10 items-center justify-center rounded-lg text-[var(--ink-soft)] hover:bg-black/5 disabled:opacity-40"
                  onClick={() => action.mutate(() => api.patch(`/api/fields/${f.id}`, { pinned: !f.pinned }))}
                >
                  {f.pinned ? <Pin size={17} className="text-accent-fg" /> : <PinOff size={17} />}
                </button>
                <button
                  aria-label={f.showOnCard ? `${f.name} auf der Karte ausblenden` : `${f.name} auf der Karte zeigen`}
                  title={f.showOnCard ? 'Auf der Karte sichtbar' : 'Nur in den Details'}
                  className="inline-flex size-10 items-center justify-center rounded-lg text-[var(--ink-soft)] hover:bg-black/5"
                  onClick={() => action.mutate(() => api.patch(`/api/fields/${f.id}`, { showOnCard: !f.showOnCard }))}
                >
                  {f.showOnCard ? <Eye size={17} /> : <EyeOff size={17} />}
                </button>
                {isOwner && (
                  <button
                    aria-label={`${f.name} löschen`}
                    className="inline-flex size-10 items-center justify-center rounded-lg text-[var(--ink-soft)] hover:text-rose-600"
                    onClick={() => {
                      if (window.confirm(`Feld „${f.name}“ und alle Werte darin löschen?`)) action.mutate(() => withFormConfirm((q) => api.del(`/api/fields/${f.id}${q}`)));
                    }}
                  >
                    <Trash2 size={16} />
                  </button>
                )}
              </li>
            ))}
          </ul>
          <Button variant="primary" onClick={() => setEditing('new')}>
            <Plus size={16} /> Neues Feld
          </Button>
          <p className="text-xs text-[var(--ink-soft)]">
            Angeheftete Felder (höchstens {MAX_PINNED_FIELDS}) stehen in den Kartendetails gleich unter „Übersicht“ – für alle in diesem Board.
          </p>
        </div>
      )}
    </Sheet>
  );
}

function FieldForm({
  field,
  snapshot,
  isOwner,
  canPin,
  onSave,
  onCancel,
}: {
  field: CustomField | null;
  snapshot: BoardSnapshot;
  isOwner: boolean;
  canPin: boolean;
  onSave: (d: { name: string; type: FieldType; options: FieldOption[]; showOnCard: boolean; pinned: boolean; config?: FieldConfig | null }) => void;
  onCancel: () => void;
}) {
  const [approval, setApproval] = useState<ApprovalConfig | null>(field?.config?.approval ?? null);
  const [sequence, setSequence] = useState<SequenceConfig>(field?.config?.sequence ?? { prefix: 'R-{JJJJ}-', digits: 4, yearly: true });
  const [backfill, setBackfill] = useState(true);
  // 0.82: Einheit, Währung, Auswertung, Formel
  const [unit, setUnit] = useState(field?.config?.unit ?? '');
  const [decimals, setDecimals] = useState<number | null>(field?.config?.decimals ?? null);
  const [currency, setCurrency] = useState(field?.config?.currency ?? 'EUR');
  const [agg, setAgg] = useState<AggregateKind | null>(field?.config?.aggregate ?? null);
  const [formulaText, setFormulaText] = useState(field?.config?.formula ? formatFieldFormula(field.config.formula.expr, snapshot.fields) : '');
  const [formulaResult, setFormulaResult] = useState<'number' | 'currency'>(field?.config?.formula?.result ?? 'number');
  // 0.85 (N04): bewusst runden, bevor andere Formeln weiterrechnen (sonst nur Anzeige)
  const [round, setRound] = useState<number | null>(field?.config?.formula?.round ?? null);
  const formula = useMemo(() => {
    if (!formulaText.trim()) return { expr: null, error: null };
    try {
      return { expr: parseFieldFormula(formulaText, snapshot.fields, field?.id ?? null), error: null };
    } catch (e) {
      return { expr: null, error: (e as Error).message };
    }
  }, [formulaText, snapshot.fields, field?.id]);
  const [name, setName] = useState(field?.name ?? '');
  const [type, setType] = useState<FieldType>(field?.type ?? 'text');
  const [showOnCard, setShowOnCard] = useState(field?.showOnCard ?? false);
  const [pinned, setPinned] = useState(field?.pinned ?? false);
  const [options, setOptions] = useState<FieldOption[]>(field?.options ?? []);
  const [optLabel, setOptLabel] = useState('');
  const hasOptions = type === 'select' || type === 'multiselect';

  const addOption = () => {
    const label = optLabel.trim();
    if (!label) return;
    const color = LABEL_COLORS[options.length % LABEL_COLORS.length] as LabelColor;
    setOptions([...options, { id: newOpId().slice(0, 12), label, color }]);
    setOptLabel('');
  };

  return (
    <form
      className="flex flex-col gap-3 p-4"
      onSubmit={(e) => {
        e.preventDefault();
        if (!name.trim()) return;
        if (hasOptions && !options.length) return toast.error('Bitte mindestens eine Auswahlmöglichkeit anlegen.');
        if (type === 'formula' && !formula.expr) return toast.error(formula.error ?? 'Bitte eine Formel eingeben, z. B. [Ist] - [Plan].');
        const measure: FieldConfig = {
          aggregate: agg,
          ...(type === 'number' || (type === 'formula' && formulaResult === 'number') ? { unit: unit.trim() || null, decimals } : {}),
          ...(type === 'currency' || (type === 'formula' && formulaResult === 'currency') ? { currency } : {}),
          ...(type === 'formula' && formula.expr ? { formula: { expr: formula.expr, result: formulaResult, ...(formulaResult === 'number' && round !== null ? { round } : {}) } } : {}),
        };
        const config =
          type === 'approval'
            ? { ...(field?.config ?? {}), approval: approval ?? DEFAULT_APPROVAL }
            : type === 'sequence'
              ? { ...(field?.config ?? {}), sequence, aggregate: agg, ...(!field && backfill ? { backfill: true } : {}) }
              : aggregateOptions(type).length || type === 'formula'
                ? { ...(field?.config ?? {}), ...measure }
                : (field?.config ?? undefined);
        onSave({ name: name.trim(), type, options: hasOptions ? options : [], showOnCard, pinned, ...(config ? { config } : {}) });
      }}
    >
      <label className="flex flex-col gap-1 text-sm">
        <span className="font-medium">Name</span>
        <input className={inputCls} autoFocus value={name} onChange={(e) => setName(e.target.value)} placeholder="z. B. Budget, Kunde, Ort" />
      </label>
      <label className="flex flex-col gap-1 text-sm">
        <span className="font-medium">Typ</span>
        <select className={inputCls} value={type} disabled={!!field} onChange={(e) => setType(e.target.value as FieldType)}>
          {FIELD_TYPES.filter((t) => t !== 'approval' || isOwner || field?.type === 'approval').map((t) => (
            <option key={t} value={t}>
              {FIELD_TYPE_LABELS[t]}
            </option>
          ))}
        </select>
        {field && <span className="text-xs text-[var(--ink-soft)]">Der Typ lässt sich nachträglich nicht ändern.</span>}
        {type === 'approval' && !field && (
          <span className="text-xs text-[var(--ink-soft)]">Eine Freigabe setzt man nicht von Hand: An der Karte gibt es „Freigeben“ und „Ablehnen“ – mit Person und Zeit im Verlauf.</span>
        )}
      </label>
      {type === 'formula' && (
        <div className="flex flex-col gap-2 rounded-xl border border-[var(--line)] p-3">
          <label className="flex flex-col gap-1 text-sm">
            <span className="font-medium">Formel</span>
            <input
              className={cx(inputCls, 'font-mono')}
              value={formulaText}
              onChange={(e) => setFormulaText(e.target.value)}
              placeholder="z. B. [Ist] - [Plan] oder [Netto] * 1,19"
            />
          </label>
          {formula.error ? (
            <p className="text-xs text-rose-600">{formula.error}</p>
          ) : (
            <p className="text-xs text-[var(--ink-soft)]">
              Felder in eckigen Klammern, Rechenzeichen + − * / und Klammern. Fehlt ein Wert, bleibt das Ergebnis leer. Zahlenfelder:{' '}
              {snapshot.fields
                .filter((f) => isNumeric(f.type) && f.id !== field?.id)
                .map((f) => `[${f.name}]`)
                .join(', ') || '– noch keine –'}
            </p>
          )}
          <label className="flex flex-col gap-1 text-sm">
            Ergebnis
            <select className={inputCls} value={formulaResult} onChange={(e) => setFormulaResult(e.target.value as 'number')}>
              <option value="number">Zahl</option>
              <option value="currency">Betrag</option>
            </select>
          </label>
          {formulaResult === 'number' ? (
            <div className="flex flex-wrap items-center gap-2 text-sm">
              <label className="flex min-h-10 items-center gap-2">
                <input type="checkbox" className="size-5 accent-[var(--color-accent)]" checked={round !== null} onChange={(e) => setRound(e.target.checked ? (decimals ?? 0) : null)} />
                Ergebnis vor dem Weiterrechnen runden auf
              </label>
              {round !== null && (
                <select aria-label="Runden auf Stellen" className={cx(inputCls, 'w-24')} value={round} onChange={(e) => setRound(Number(e.target.value))}>
                  {[0, 1, 2, 3, 4, 5, 6].map((d) => (
                    <option key={d} value={d}>
                      {d} {d === 1 ? 'Stelle' : 'Stellen'}
                    </option>
                  ))}
                </select>
              )}
              <span className="w-full text-xs text-[var(--ink-soft)]">
                Ohne Haken rechnet KaBa genau weiter; die Nachkommastellen unten ändern nur die Anzeige.
              </span>
            </div>
          ) : (
            <span className="text-xs text-[var(--ink-soft)]">Beträge rechnet KaBa auf den Cent genau (kaufmännisch gerundet).</span>
          )}
        </div>
      )}
      {(type === 'number' || (type === 'formula' && formulaResult === 'number')) && (
        <div className="flex flex-wrap gap-2">
          <label className="flex flex-col gap-1 text-sm">
            Einheit (optional)
            <input className={cx(inputCls, 'w-32')} maxLength={12} value={unit} onChange={(e) => setUnit(e.target.value)} placeholder="Std., km, Stück" />
          </label>
        </div>
      )}
      {(type === 'currency' || (type === 'formula' && formulaResult === 'currency')) && (
        <label className="flex flex-col gap-1 text-sm">
          Währung
          <select className={cx(inputCls, 'w-32')} value={currency} onChange={(e) => setCurrency(e.target.value)}>
            {CURRENCIES.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        </label>
      )}
      {/* 0.86: Auswertung und Anzeige-Stellen unter „Weitere Einstellungen“ – Name, Typ, Einheit/Währung zuerst */}
      {(aggregateOptions(type).length > 0 || type === 'number' || (type === 'formula' && formulaResult === 'number')) && (
        <details className="rounded-xl border border-[var(--line)] px-3" open={agg !== null || decimals !== null}>
          <summary className="flex min-h-11 cursor-pointer items-center text-sm font-medium">Weitere Einstellungen</summary>
          <div className="flex flex-col gap-3 pb-3">
            {(type === 'number' || (type === 'formula' && formulaResult === 'number')) && (
              <label className="flex flex-col gap-1 text-sm">
                Nachkommastellen (Anzeige)
                <select className={cx(inputCls, 'w-32')} value={decimals ?? ''} onChange={(e) => setDecimals(e.target.value === '' ? null : Number(e.target.value))}>
                  <option value="">wie eingegeben</option>
                  {[0, 1, 2, 3].map((d) => (
                    <option key={d} value={d}>
                      {d}
                    </option>
                  ))}
                </select>
              </label>
            )}
            {aggregateOptions(type).length > 0 && (
            <label className="flex flex-col gap-1 text-sm">
              <span className="font-medium">Auswertung</span>
              <select className={inputCls} value={agg ?? ''} onChange={(e) => setAgg((e.target.value || null) as AggregateKind | null)}>
                <option value="">keine</option>
                {aggregateOptions(type).map((k) => (
                  <option key={k} value={k}>
                    {k === 'count' ? (type === 'checkbox' ? 'Anzahl angehakt' : 'Anzahl ausgefüllt') : AGGREGATE_LABEL[k]}
                  </option>
                ))}
              </select>
              <span className="text-xs text-[var(--ink-soft)]">Erscheint im Spaltenkopf, in der Tabelle, in Zeilen und im PDF-Bericht – nur wenn Karten Werte haben.</span>
            </label>
            )}
          </div>
        </details>
      )}
      {type === 'sequence' && (
        <div className="flex flex-col gap-2 rounded-xl border border-[var(--line)] p-3">
          <p className="text-xs text-[var(--ink-soft)]">KaBa vergibt die Nummer beim Anlegen einer Karte – fortlaufend, ohne Lücken durch gleichzeitiges Anlegen. Ändern lässt sie sich nicht.</p>
          <div className="flex flex-wrap items-end gap-2">
            <label className="flex flex-col gap-1 text-sm">
              Präfix
              <input className={cx(inputCls, 'w-36')} maxLength={20} value={sequence.prefix} onChange={(e) => setSequence({ ...sequence, prefix: e.target.value })} />
            </label>
            <label className="flex flex-col gap-1 text-sm">
              Stellen
              <input
                className={cx(inputCls, 'w-20')}
                type="number"
                min={1}
                max={8}
                value={sequence.digits}
                onChange={(e) => setSequence({ ...sequence, digits: Math.max(1, Math.min(8, Number(e.target.value) || 4)) })}
              />
            </label>
            <span className="min-h-10 py-2 font-mono text-sm">→ {formatSequence(sequence, 1, new Date().getFullYear())}</span>
          </div>
          <span className="text-xs text-[var(--ink-soft)]">{'{JJJJ}'} wird zum Jahr, {'{JJ}'} zu den letzten zwei Ziffern.</span>
          <label className="flex min-h-10 items-center gap-2 text-sm">
            <input type="checkbox" className="size-5 accent-[var(--color-accent)]" checked={sequence.yearly} onChange={(e) => setSequence({ ...sequence, yearly: e.target.checked })} />
            Jedes Jahr wieder bei 1 beginnen
          </label>
          {!field && (
            <label className="flex min-h-10 items-center gap-2 text-sm">
              <input type="checkbox" className="size-5 accent-[var(--color-accent)]" checked={backfill} onChange={(e) => setBackfill(e.target.checked)} />
              Bestehende Karten jetzt nummerieren (in der Reihenfolge ihrer Anlage)
            </label>
          )}
        </div>
      )}
      {type === 'approval' &&
        (isOwner ? (
          <ApprovalSettings snapshot={snapshot} selfId={field?.id ?? null} value={approval} onChange={setApproval} />
        ) : (
          <p className="text-xs text-[var(--ink-soft)]">Wer freigeben darf, legen Besitzer*innen des Boards fest.</p>
        ))}
      {hasOptions && (
        <div className="flex flex-col gap-2">
          <span className="text-sm font-medium">Auswahlmöglichkeiten</span>
          <ul className="flex flex-wrap gap-1.5">
            {options.map((o) => (
              <li key={o.id} className={cx('inline-flex min-h-9 items-center gap-1 rounded-lg pl-2.5 text-sm text-white', LABEL_BG[o.color])}>
                {o.label}
                <button
                  type="button"
                  aria-label={`${o.label} entfernen`}
                  className="inline-flex size-8 items-center justify-center"
                  onClick={() => setOptions(options.filter((x) => x.id !== o.id))}
                >
                  <X size={14} />
                </button>
              </li>
            ))}
          </ul>
          <div className="flex gap-2">
            <input
              className={inputCls}
              value={optLabel}
              placeholder="Neue Option …"
              onChange={(e) => setOptLabel(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  addOption();
                }
              }}
            />
            <Button type="button" onClick={addOption} aria-label="Option hinzufügen">
              <Plus size={16} />
            </Button>
          </div>
        </div>
      )}
      <label className="flex min-h-11 items-center gap-2.5 text-sm">
        <input type="checkbox" checked={showOnCard} onChange={(e) => setShowOnCard(e.target.checked)} className="size-5 accent-[var(--color-accent)]" />
        Auf der Kartenvorderseite zeigen
      </label>
      <label className={cx('flex min-h-11 items-center gap-2.5 text-sm', !canPin && 'opacity-60')}>
        <input
          type="checkbox"
          checked={pinned}
          disabled={!canPin && !pinned}
          onChange={(e) => setPinned(e.target.checked)}
          className="size-5 accent-[var(--color-accent)]"
        />
        <span>
          In den Kartendetails unter „Übersicht“ anheften
          {!canPin && !pinned && <span className="block text-xs text-[var(--ink-soft)]">Es sind schon {MAX_PINNED_FIELDS} Felder angeheftet.</span>}
        </span>
      </label>
      <div className="flex justify-end gap-2">
        <Button type="button" variant="ghost" onClick={onCancel}>
          Abbrechen
        </Button>
        <Button type="submit" variant="primary">
          Speichern
        </Button>
      </div>
    </form>
  );
}
